<template>
  <div class="page-tab withdraw-info-container" style="height: 600px">
    <SchemaTablePage
      :form-props="{ labelPosition: 'left' }"
      ref="tablePageRef"
      layout-mode="dialog"
      :immediate="false"
      :search-schema="searchSchema"
      :default-query="defaultQuery"
      :request="request"
      :build-params="buildParams"
      :parse-result="parseResult"
    >
      <template #table-content="{ tableProps, tableData, loading }">
        <el-table
          v-loading="loading"
          :data="tableData"
          v-bind="tableProps"
          size="small"
          border
          stripe
          fit
          highlight-current-row
          style="width: 100%"
          :summary-method="getSummaries"
          show-summary
        >
          <el-table-column :resizable="false" prop="orderNo" label="订单号" min-width="180"></el-table-column>
          <el-table-column :resizable="false" prop="thirdOrderNo" label="三方订单号" min-width="180"></el-table-column>
          <el-table-column :resizable="false" prop="orderAmount" label="订单金额" width="100">
            <template slot-scope="scope"> {{ scope.row.orderAmount | centsToYuan }}</template>
          </el-table-column>
          <el-table-column :resizable="false" prop="accountType" label="账号类型" width="110"></el-table-column>
          <el-table-column :resizable="false" prop="bankName" label="银行名称" min-width="150"></el-table-column>
          <el-table-column :resizable="false" prop="account" label="提款账号" min-width="120"></el-table-column>
          <el-table-column :resizable="false" prop="paymentChannelName" label="代付通道" width="110"></el-table-column>
          <el-table-column :resizable="false" prop="applyTime" label="申请时间" width="150"></el-table-column>
          <el-table-column :resizable="false" prop="completeTime" label="完成时间" width="150"></el-table-column>
          <el-table-column :resizable="false" fixed="right" prop="status" label="订单状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="getTagType(scope.row.status)?.type">{{ getTagType(scope.row.status)?.label }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </SchemaTablePage>
  </div>
</template>

<script>
import SchemaTablePage from '@/components/SchemaTablePage'
import { getPayoutHistory } from '@/api/finance/moneyManagement'
import { WITHDRAW_STATUS_OPTIONS } from '@/const'
import { centsToYuan } from '@/utils/amount'
import { formatTimestamp, toUnixSeconds } from '@/utils/time'

export default {
  name: 'ManageWithdrawInfoTabV2',
  components: { SchemaTablePage },
  props: {
    resetKey: { type: Number, default: 0 },
    targetInfo: { type: Object, default: () => ({}) },
    isActive: { type: Boolean, default: false },
  },
  data() {
    return {
      defaultQuery: {
        status: 0,
        order_id: '',
        third_order_id: '',
        applyDateRange: [],
        finishDateRange: [],
      },
      searchSchema: [
        {
          prop: 'order_id',
          label: '订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'third_order_id',
          label: '三方订单号',
          component: 'input',
          componentProps: { placeholder: '请输入三方订单号', clearable: true },
        },
        {
          prop: 'status',
          label: '订单状态',
          component: 'select',
          componentProps: { clearable: true },
          options: WITHDRAW_STATUS_OPTIONS,
        },
        {
          prop: 'applyDateRange',
          label: '申请时间',
          component: 'datetime-range',
          span: { xs: 24, sm: 12, md: 16, lg: 8, xl: 6 },
        },
        {
          prop: 'finishDateRange',
          label: '完成时间',
          component: 'datetime-range',
          span: { xs: 24, sm: 12, md: 16, lg: 8, xl: 6 },
        },
        {
          component: 'actions',
        },
      ],
    }
  },
  computed: {
    request() {
      return params => {
        if (!this.targetInfo?.username) {
          return Promise.resolve({ code: 0, data: { list: [], total: 0 } })
        }
        return getPayoutHistory(params)
      }
    },
    buildParams() {
      return ({ query, page, limit }) => {
        const status = query?.status
        let statusP = []
        if (status === 0 || status === '') statusP = []
        else if (status === 4) statusP = [1, 2, 3, 4]
        else statusP = [status]

        const params = {
          page,
          page_size: limit,
          status: statusP,
          username: this.targetInfo?.username || '',
          order_id: query?.order_id || '',
          third_order_id: query?.third_order_id || '',
        }

        if (query?.applyDateRange?.length === 2) {
          const startAt = toUnixSeconds(query.applyDateRange[0])
          const endAt = toUnixSeconds(query.applyDateRange[1])
          if (startAt) params.start_at = startAt
          if (endAt) params.end_at = endAt
        }

        if (query?.finishDateRange?.length === 2) {
          const s = toUnixSeconds(query.finishDateRange[0])
          const e = toUnixSeconds(query.finishDateRange[1])
          if (s) params.finish_start_at = s
          if (e) params.finish_end_at = e
        }
        return params
      }
    },
    parseResult() {
      return res => {
        if (res && res.code === 0 && res.data) {
          const list = res.data.list || []
          return {
            list: list.map(item => this.mapWithdrawItem(item)),
            total: res.data.total || 0,
          }
        }
        this.$message.error(res?.msg || res?.message || '获取提现记录失败')
        return { list: [], total: 0 }
      }
    },
  },
  watch: {
    resetKey() {
      this.$nextTick(() => this.$refs.tablePageRef?.reset?.())
    },
    isActive: {
      handler(v) {
        if (!v || !this.targetInfo?.username) return
        this.$nextTick(() => this.$refs.tablePageRef?.reload?.())
      },
      immediate: true,
    },
    targetInfo: {
      handler() {
        if (!this.isActive || !this.targetInfo?.username) return
        this.$nextTick(() => this.$refs.tablePageRef?.reload?.())
      },
      deep: true,
    },
  },
  methods: {
    getTagType(status) {
      let _status = 0
      if ([1, 2, 3, 4].includes(status)) {
        _status = 4
      } else {
        _status = status
      }
      const opt = WITHDRAW_STATUS_OPTIONS.find(o => o.value === _status) || {}
      return { label: opt.label || '', type: opt.type || '' }
    },
    mapWithdrawItem(item) {
      return {
        orderNo: item.order_id ?? item.id ?? '',
        thirdOrderNo: item.third_order_id || '',
        orderAmount: item.amount,
        applyTime: formatTimestamp(item.created_at),
        completeTime: formatTimestamp(item.updated_at),
        status: item.status,
        account: item.account || '',
        accountType: item.account_type || '',
        paymentChannelName: item.payment_channel_name || '',
        bankName: item.bank_name || '',
      }
    },
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '小计（当前页）'
          return
        }
        if (column.property === 'orderAmount') {
          const values = data.map(item => parseFloat(item[column.property]) || 0)
          const s = values.reduce((a, b) => a + b, 0)
          sums[index] = centsToYuan(s)
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
  },
}
</script>
